// frontend/src/pages/admin/SiteImagesPage.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import siteImageService from "../../services/siteImageService";

const RECOMMENDED_DIMS = {
  banner: "1920 x 800px",
  inline: "800 x 800px or 800 x 600px",
};

const SiteImagesPage = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-slot editing state: { [slot]: { file, preview, alt, saving } }
  const [editState, setEditState] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await siteImageService.getAllSiteImages();
      setImages(data.images || {});
    } catch (err) {
      setError(err.message || "Failed to load site images");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (slot, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error(t("siteImages.errors.invalidFormat"));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("siteImages.errors.fileTooLarge"));
      return;
    }

    const preview = URL.createObjectURL(file);
    setEditState((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        file,
        preview,
        alt: prev[slot]?.alt ?? images[slot]?.alt ?? "",
      },
    }));
  };

  const handleAltChange = (slot, value) => {
    setEditState((prev) => ({
      ...prev,
      [slot]: {
        ...prev[slot],
        alt: value,
      },
    }));
  };

  const handleCancel = (slot) => {
    if (editState[slot]?.preview) {
      URL.revokeObjectURL(editState[slot].preview);
    }
    setEditState((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
    // Reset the file input
    if (fileInputRefs.current[slot]) {
      fileInputRefs.current[slot].value = "";
    }
  };

  const handleSave = async (slot) => {
    const state = editState[slot];
    if (!state?.file && state?.alt === undefined) return;

    setEditState((prev) => ({
      ...prev,
      [slot]: { ...prev[slot], saving: true },
    }));

    try {
      const formData = new FormData();
      if (state.file) {
        formData.append("image", state.file);
      }
      if (state.alt !== undefined) {
        formData.append("alt", state.alt);
      }

      await siteImageService.updateSiteImage(slot, formData);
      toast.success(t("siteImages.success.updated"));

      // Clean up preview URL
      if (state.preview) {
        URL.revokeObjectURL(state.preview);
      }

      // Clear edit state for this slot
      setEditState((prev) => {
        const next = { ...prev };
        delete next[slot];
        return next;
      });

      // Refresh images
      await fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.message || t("siteImages.errors.uploadFailed"));
    } finally {
      setEditState((prev) => ({
        ...prev,
        [slot]: prev[slot] ? { ...prev[slot], saving: false } : undefined,
      }));
    }
  };

  const getDisplayUrl = (url) => {
    if (!url) return "";
    // For local paths starting with /uploads, prepend the backend URL
    if (url.startsWith("/uploads")) {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || "";
      return `${backendUrl}${url}`;
    }
    // For default static images (like /images/hero.jpg), they won't resolve here
    // but that's OK — they're website-side defaults
    return url;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const slots = Object.entries(images);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t("siteImages.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("siteImages.subtitle")}
      </Typography>

      <Grid container spacing={3}>
        {slots.map(([slot, img]) => {
          const edit = editState[slot];
          const isEditing = !!edit?.file || edit?.alt !== undefined;
          const isSaving = edit?.saving;

          return (
            <Grid item xs={12} sm={6} md={4} key={slot}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                {/* Current or preview image */}
                <CardMedia
                  component="img"
                  height={img.imageType === "banner" ? 180 : 200}
                  image={edit?.preview || getDisplayUrl(img.imageUrl)}
                  alt={edit?.alt ?? img.alt ?? slot}
                  sx={{ objectFit: "cover" }}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                      {img.pageTitle}
                    </Typography>
                    <Chip
                      label={img.imageType === "banner" ? t("siteImages.typeBanner") : t("siteImages.typeInline")}
                      size="small"
                      color={img.imageType === "banner" ? "primary" : "secondary"}
                    />
                  </Box>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    consciencegems.ch{img.pagePath}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    {t("siteImages.recommendedSize")}: {RECOMMENDED_DIMS[img.imageType]}
                  </Typography>

                  {/* Alt text input */}
                  <TextField
                    label={t("siteImages.altText")}
                    size="small"
                    fullWidth
                    value={edit?.alt ?? img.alt ?? ""}
                    onChange={(e) => handleAltChange(slot, e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  {/* File input (hidden) */}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    ref={(el) => (fileInputRefs.current[slot] = el)}
                    onChange={(e) => handleFileSelect(slot, e)}
                  />

                  {/* Action buttons */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRefs.current[slot]?.click()}
                      disabled={isSaving}
                    >
                      {t("siteImages.replaceImage")}
                    </Button>

                    {isEditing && (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={() => handleSave(slot)}
                          disabled={isSaving}
                        >
                          {t("buttons.save")}
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<CloseIcon />}
                          onClick={() => handleCancel(slot)}
                          disabled={isSaving}
                        >
                          {t("buttons.cancel")}
                        </Button>
                      </>
                    )}
                  </Box>

                  {/* Last updated */}
                  {img.updatedAt && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      {t("siteImages.lastUpdated")}: {new Date(img.updatedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SiteImagesPage;
